import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { usePortfolio } from '@/lib/portfolio';
import { CustomSelect } from '@/components/CustomSelect';
import { LayoutDashboard, Building2, Banknote, Landmark, FileSignature, ShieldAlert, Lightbulb, Target, GitBranch, FlaskConical, FileText, Settings, LogOut, Menu, X, Bell, Home, Globe, ShoppingBag, Newspaper } from 'lucide-react';

const nav = [
  { path: '/home', label: 'Home', icon: Home },
  { path: '/portfolio', label: 'Portfolio', icon: LayoutDashboard },
  { path: '/cashflow', label: 'Cashflow & P&L', icon: Banknote },
  { path: '/debt', label: 'Debt & Financing', icon: Landmark },
  { path: '/contracts', label: 'Contracts', icon: FileSignature },
  { path: '/risk', label: 'Risk & Spreiding', icon: ShieldAlert },
  { path: '/opportunities', label: 'Opportunities', icon: Lightbulb },
  { path: '/strategy', label: 'Strategy', icon: Target },
  { path: '/pipeline', label: 'Deal Pipeline', icon: GitBranch },
  { path: '/market', label: 'Market Deals', icon: ShoppingBag },
  { path: '/scenarios', label: 'Scenario Lab', icon: FlaskConical },
  { path: '/macro', label: 'Macro Intel', icon: Globe },
  { path: '/news', label: 'News Intel', icon: Newspaper },
  { path: '/reports', label: 'Reports', icon: FileText },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { portfolios, activeId, switchPortfolio } = usePortfolio();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const pfOptions = [
    { value: 'all', label: 'All Portfolios', sub: 'Consolidated' },
    ...portfolios.map(p => ({ value: p.portfolio_id, label: p.name, sub: p.entity_type || '' }))
  ];

  return (
    <div className="min-h-screen bg-[#F4F1EB] flex">
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[220px] bg-white border-r border-[#E2DDD3] flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ boxShadow: '4px 0 12px rgba(0,0,0,0.02)' }}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-[#E2DDD3]">
          <Link to="/home" data-testid="nav-brand" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#1F3A34] flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
            <span className="font-serif text-[15px] font-semibold text-[#1C1C1C]">EstaFlow</span>
          </Link>
        </div>

        {/* Portfolio Selector */}
        {portfolios.length > 0 && (
          <div className="px-3 py-3 border-b border-[#E2DDD3]">
            <CustomSelect value={activeId || 'all'} onChange={switchPortfolio} testId="global-portfolio-selector"
              options={pfOptions} />
          </div>
        )}

        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {nav.map(item => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link key={item.path} to={item.path} data-testid={`nav-${item.path.slice(1)}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 px-3 py-[8px] rounded-lg text-[12px] font-sans transition-all duration-200 ${
                  active ? 'bg-[#E6EFEA] text-[#1F3A34] font-medium' : 'text-[#5A5A5A] hover:bg-[#F4F1EB] hover:text-[#1C1C1C]'
                }`}>
                <item.icon className="w-[16px] h-[16px]" strokeWidth={1.6} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-[#E2DDD3]">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-[#E6EFEA] flex items-center justify-center text-[11px] font-sans font-semibold text-[#1F3A34]">{user?.name?.[0] || '?'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-[#1C1C1C] font-sans font-medium truncate">{user?.name}</p>
              <p className="text-[9px] text-[#8C877F] truncate">{user?.email}</p>
            </div>
            <button data-testid="logout-button" onClick={async () => { await logout(); window.location.href = '/'; }} className="text-[#8C877F] hover:text-[#8A3C3C] transition-colors">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </aside>

      <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
        className="lg:hidden fixed top-4 left-4 z-[60] p-2 bg-white border border-[#E2DDD3] rounded-lg text-[#5A5A5A] shadow-sm">
        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
      </button>
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/10" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-h-screen overflow-y-auto">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 md:py-10">{children}</div>
      </main>
    </div>
  );
}
